import {
	ChangeEvent,
	Fragment,
	ReactElement,
	useEffect,
	useRef,
	useState,
} from 'react';
import { nanoid } from 'nanoid';
import classnames from 'classnames';
import { Checkbox, IconButton } from '@upmc/ims.component-library';
import classes from './Multiselect.module.scss';
import { Headline3, Headline6 } from '../../MDC/Typography/Typography';
import ChipSet from '../../Chips/ChipSet/ChipSet';
import Chip from '../../Chips/Chip/Chip';
import Icon from '../../Icon/Icon';
import List, { ListItem, MDCListDivider } from '../../MDC/List/List';
import StyleAuxiliary from '../../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import TextField from '../../MDC/TextField/TextField';
import WithLoadingHandler from '../../../../hoc/withLoadingHandler/withLoadingHandler';
import { MultiselectTypes } from './_types';
import { DataTable } from '../../MDC/DataTable/DataTable';
import { multiselectPropTypes } from './_types';
import { formatForID } from '../../../../utils/strings/StringUtil';

/**
 * The Multi-select component displays rows of data for bulk consumption.
 * Selecting an option will display a Chip in the header.
 * There are two variants:  a table-based variant that consumes the `DataTable` component and a list-based variant that utilizes the `List` component
 * @param {Object} props JSX attributes passed to component.
 * @returns {ReactElement} rendered component.
 */
const Multiselect = ({ withoutFormik = false, ...props }) => {
	/**
	 * @typedef {Boolean} isOpen indicates whether the "content drawer" is displayed or not
	 */
	/**
	 * @typedef {Function} setIsOpen sets the `isOpen` state
	 */
	/**
	 * @type {[isOpen, setIsOpen]}
	 */
	const [isOpen, setIsOpen] = useState(false);

	/**
	 * @typedef {Boolean} isLoading flag to indicate when page is loading
	 */
	/**
	 * @typedef {Function} setIsLoading sets the `isLoading` state
	 */
	/**
	 * @type {[isLoading, setIsLoading]}
	 */
	const [isLoading, setIsLoading] = useState(false);

	/**
	 * @typedef {Boolean} isLoadingResults flag to indicate when content is loading
	 */
	/**
	 * @typedef {Function} setIsLoadingResults sets the `isLoadingResults` state
	 */
	/**
	 * @type {[isLoadingResults, setIsLoadingResults]}
	 */
	const [isLoadingResults, setIsLoadingResults] = useState(false);

	/**
	 * @typedef {Object[]} optionsList array of content consisting of either  {value: [string], isSelected: [bool], isDisplayed: bool} for LIST or {value: {'header': {Value: [string], Validation: {Key: [string], Value: {ErrorMessage: [string]}}}}, isSelected: [bool], isDisplayed: bool} for TABLE
	 */
	/**
	 * @typedef {Function} setOptionsList sets the `optionsList` state
	 */
	/**
	 * @type {[optionsList, setOptionsList]}
	 */
	const [optionsList, setOptionsList] = useState([]);

	/**
	 * @typedef {String} searchTextFieldHelp help text displayed for the search box
	 */
	/**
	 * @typedef {Function} setSearchTextFieldHelp sets the `searchTextFieldHelp` state
	 */
	/**
	 * @type {[searchTextFieldHelp, setSearchTextFieldHelp]}
	 */
	const [searchTextFieldHelp, setSearchTextFieldHelp] = useState(
		"Press 'Enter' or click icon to search."
	);

	/**
	 * @typedef {String} filterTextFieldHelp help text displayed for the filter box
	 */
	/**
	 * @typedef {Function} setFilterTextFieldHelp sets the `filterTextFieldHelp` state
	 */
	/**
	 * @type {[filterTextFieldHelp, setFilterTextFieldHelp]}
	 */
	const [filterTextFieldHelp, setFilterTextFieldHelp] = useState();

	/**
	 * @typedef {String} selectedIndex currently selected option in the list
	 */
	/**
	 * @typedef {Function} setSelectedIndex sets the `selectedIndex` state
	 */
	/**
	 * @type {[selectedIndex, setSelectedIndex]}
	 */
	const [selectedIndex, setSelectedIndex] = useState([-1]);

	/**
	 * ref for the search box
	 */
	const refTextFieldSearch = useRef(null);

	/**
	 * event handler for opening and closing the contents drawer
	 */
	const handleOpenToggle = () => {
		//disable toggle if props.settings.isDisabled = true
		if (!props.settings.isDisabled) {
			setIsOpen(!isOpen);
		}
	};

	/**
	 * event handler for pressing the 'Select All' button
	 * @param {ChangeEvent<HTMLInputElement>} e event object
	 */
	const handleSelectAll = e => {
		const newOptionsList = [...optionsList];
		newOptionsList.forEach(opt => {
			if (e.target.checked) {
				if (
					props.settings.selectAllHandler &&
					props.settings.selectAllHandler(opt)
				) {
					opt.isSelected = true;
				}
			} else {
				opt.isSelected = false;
			}
		});
		setOptionsList(newOptionsList);
	};

	/**
	 * event handler for selecting a row in the contents
	 * @param {Object} identifier either the index in the List or the key in the Table
	 */
	const handleRowCheck = identifier => {
		let newOptionsList = [...optionsList];
		let index;
		switch (props.settings.type) {
			case MultiselectTypes.LIST:
				index = identifier;
				newOptionsList[index].isSelected =
					!newOptionsList[index].isSelected;
				break;
			case MultiselectTypes.TABLE:
				index = newOptionsList.findIndex(
					opt => opt.key === identifier.toString()
				);
				if (index > -1) {
					newOptionsList[index].isSelected =
						!newOptionsList[index].isSelected;
				}
				break;
			default:
				break;
		}
		setOptionsList(newOptionsList);
	};

	/**
	 * event handler to respond to text typed in the filter box
	 * @param {ChangeEvent<HTMLInputElement>} e event object
	 */
	const handleFilter = e => {
		//check each of the original options against props filter function
		//if pass, set list of options (locally)
		//if(e.target.value is empty or null), reset local options list to original options
		//props.settings.filterHandler
		setIsLoadingResults(true);
		const value = e.target.value;
		if (value && value.length > 0) {
			let newOptionsList = [...optionsList];
			switch (props.settings.type) {
				case MultiselectTypes.LIST:
					newOptionsList = optionsList.filter(o => {
						return props.settings.filterHandler(value, o.value);
					});
					break;
				case MultiselectTypes.TABLE:
					newOptionsList = optionsList.filter(o => {
						return Object.keys(o.value).some(valKey => {
							return props.settings.filterHandler(
								value,
								o.value[valKey].Value
							);
						});
					});
					break;
				default:
					break;
			}

			setOptionsList(newOptionsList);
		} else {
			//reset
			resetOptionsList();
		}
		setIsLoadingResults(false);
	};

	/**
	 * event handler to respond to text typed in the search box
	 * @param {ChangeEvent<HTMLInputElement>} e event object
	 */
	const handleSearch = async e => {
		setIsLoadingResults(true);
		const newValues = await props.settings.searchHandler(e.target.value);
		addOptions(newValues, false, true);
		setIsLoadingResults(false);
	};

	/**
	 * add new options to the state list
	 * @param {Object[]} newValues new array of contents to be added to the options list
	 * @param {Boolean} filter is filter enabled?
	 * @param {Boolean} search is search enabled?
	 */
	const addOptions = (newValues, filter = false, search = false) => {
		let newOptionsList = [];
		if (newValues && newValues.length > 0) {
			newValues.forEach(opt => {
				newOptionsList.push({
					value: opt,
					isSelected: false,
					isDisplayed: true,
				});
			});

			if (search) {
				setSearchTextFieldHelp(
					"Press 'Enter' or click icon to search."
				);
			}
			if (filter) {
				setFilterTextFieldHelp('');
			}
		} else {
			//no results to display
			if (search) {
				setSearchTextFieldHelp('Increase search specificity.');
			}
			if (filter) {
				setFilterTextFieldHelp('No results.');
			}
		}
		setOptionsList(newOptionsList);
	};

	/**
	 * reset the options list to the original list provided in `props`
	 */
	const resetOptionsList = () => {
		switch (props.settings.type) {
			case MultiselectTypes.LIST:
				if (props.options && props.options.length > 0) {
					let newOptionsList = [];
					props.options.forEach(opt => {
						newOptionsList.push({
							value: opt.value,
							isSelected: opt.isSelected ? opt.isSelected : false,
							isDisplayed: true,
						});
					});
					setOptionsList(newOptionsList);
				}
				break;
			case MultiselectTypes.TABLE:
				if (
					props.options &&
					props.options.values &&
					props.options.values.length > 0
				) {
					let newOptionsList = [];
					props.options.values.forEach(opt => {
						const sanitizedOpt = { ...opt };
						if ('Key' in sanitizedOpt) {
							delete sanitizedOpt.Key;
						}
						if ('isSelected' in sanitizedOpt) {
							delete sanitizedOpt.isSelected;
						}
						newOptionsList.push({
							key: opt.Key ? opt.Key : null,
							value: sanitizedOpt,
							isSelected: opt.isSelected ? opt.isSelected : false,
							isDisplayed: true,
						});
					});
					setOptionsList(newOptionsList);
				}
				break;

			default:
				break;
		}
		setIsLoading(false);
	};

	/**
	 * lifecycle hook to reset the options list in local state when options in `props` changes
	 * triggers:  `props.options`, `props.settings.type`
	 */
	useEffect(() => {
		setIsLoading(true);
		resetOptionsList();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.options, props.settings.type]);

	/**
	 * checks to see if any options have been selected
	 * @returns {boolean} should display action label
	 */
	const displayActionLabel = () => {
		return optionsList.some(opt => opt.isSelected);
	};

	/**
	 * generate the proper UI for the given Multiselect type
	 * @returns {ReactElement}
	 */
	const generateOptionsUI = () => {
		switch (props.settings.type) {
			case MultiselectTypes.LIST:
				return (
					<WithLoadingHandler
						showChildren={!isLoadingResults}
						isLoading={isLoadingResults}
					>
						<StyleAuxiliary classes={classes['multi-select__list']}>
							<List
								xlarge
								checkboxList={
									!(
										props.settings.isDisabled ||
										props.settings.isReadOnly
									)
								}
								singleSelection={false}
								selectedIndex={
									props.settings.isDisabled ||
									props.settings.isReadOnly
										? null
										: selectedIndex
								}
								handleSelect={(activatedIndex, allSelected) => {
									setSelectedIndex(allSelected);
									if (props.settings.selectHandler) {
										props.settings.selectHandler(
											activatedIndex
										);
									}
								}}
								name='list-contents'
								disabled={
									props.settings.isDisabled ||
									props.settings.isReadOnly
								}
							>
								{optionsList.map((el, index) => {
									let divider = <MDCListDivider tag='div' />;
									if (index === optionsList.length - 1) {
										divider = null;
									}

									//return the checkbox and label
									return (
										<div
											key={'listitem_' + el.value}
											onClick={
												props.settings.isDisabled ||
												props.settings.isReadOnly
													? null
													: () => {
															handleRowCheck(
																index
															);
													  }
											}
										>
											<ListItem role='listitem'>
												{!props.settings.isReadOnly ? (
													<Checkbox
														checked={el.isSelected}
														disabled={
															props.settings
																.isDisabled
														}
														inputProps={{
															id: `multi-select${
																props.title &&
																typeof props.title !==
																	'undefined'
																	? `__${formatForID(
																			props.title
																	  )}`
																	: ''
															}__option-${formatForID(
																el.value
															)}__checkbox`,
														}}
														label={el.value}
													/>
												) : (
													el.value
												)}
											</ListItem>
											{divider}
										</div>
									);
								})}
							</List>
						</StyleAuxiliary>
					</WithLoadingHandler>
				);
			case MultiselectTypes.TABLE:
				let rows = [];
				optionsList.forEach(opt => {
					rows.push({
						ActionCells: [
							{
								BindTo: 0,
								Checked: opt.isSelected,
								Key: nanoid(),
							},
						],
						Cells: {
							...opt.value,
						},
						Key: opt.key ? opt.key : nanoid(),
					});
				});

				let headers = [];
				Object.keys(props.options.headers).forEach(id => {
					headers.push({
						Value: props.options.headers[id],
					});
				});

				return (
					<WithLoadingHandler
						showChildren={!isLoadingResults}
						isLoading={isLoadingResults}
					>
						<StyleAuxiliary>
							<DataTable
								actions={[
									{
										Key: nanoid(),
										Type: 'checkbox',
										onChangedHandler: (_, __, row) => {
											handleRowCheck(row.Key);
											if (props.settings.selectHandler) {
												props.settings.selectHandler(
													row
												);
											}
										},
									},
								]}
								rows={rows}
								filteredOptions={[]}
								headers={headers}
								isReadOnly={props.settings.isReadOnly}
								withoutFormik={withoutFormik}
							/>
						</StyleAuxiliary>
					</WithLoadingHandler>
				);
			default:
				return null;
		}
	};

	//styles
	let styleToolBar = null;
	if (props.settings.isSearchable && optionsList.length < 1) {
		styleToolBar = classes['multi-select__tool-bar--empty'];
	} else {
		styleToolBar = [
			classes['multi-select__tool-bar--filled'],
			'mdc-form-field',
		].join(' ');
	}

	/**
	 * Based on the Multiselect type, navigate the object and return the label to be displayed on the Chip
	 * @param {Object} item
	 */
	const getChipValue = item => {
		switch (props.settings.type) {
			case MultiselectTypes.LIST:
				return item.value;
			case MultiselectTypes.TABLE:
				return Object.values(item.value)[0].Value;
			default:
				return '';
		}
	};

	//UI
	/**
	 * displays selected items in Chips
	 */
	const selectionsUI = (
		<ChipSet>
			{optionsList.map((item, index) => {
				return item.isSelected ? (
					<Chip
						key={'chip_' + index}
						label={getChipValue(item)}
						trailingIcon={
							props.settings.isReadOnly ||
							props.settings.isDisabled ? null : (
								<Icon
									icon='delete'
									id={`multi-select${
										props.title &&
										typeof props.title !== 'undefined'
											? `__${formatForID(props.title)}`
											: ''
									}__option-${formatForID(
										getChipValue(item)
									)}__chip__remove`}
								/>
							)
						}
						handleTrailingIconInteraction={() => {
							if (props.settings.chipHandler) {
								props.settings.chipHandler(item);
							}
							handleRowCheck(index);
						}}
						disabled={
							props.settings.isReadOnly ||
							props.settings.isDisabled
						}
						data-test='chipset'
					/>
				) : null;
			})}
		</ChipSet>
	);

	/**
	 * "(de)select all" checkbox
	 */
	const selectAllUI = props.settings.isSelectAllable ? (
		<Checkbox
			title={
				optionsList.some(opt => opt.isSelected)
					? 'Deselect All'
					: 'Select All'
			}
			aria-label={
				optionsList.some(opt => opt.isSelected)
					? 'Deselect All'
					: 'Select All'
			}
			inputProps={{
				id: `multi-select${
					props.title && typeof props.title !== 'undefined'
						? `__${formatForID(props.title)}`
						: ''
				}__select-all`,
			}}
			disabled={
				optionsList.length === 0 ||
				props.settings.isReadOnly ||
				props.settings.isDisabled
			}
			checked={optionsList.every(opt => opt.isSelected)}
			indeterminate={
				optionsList.some(opt => opt.isSelected) &&
				optionsList.some(opt => !opt.isSelected)
			}
			label={
				optionsList.some(opt => opt.isSelected)
					? 'Deselect All'
					: 'Select All'
			}
			onChange={e => handleSelectAll(e)}
			data-test='selectAll'
		/>
	) : null;

	/**
	 * filter textbox
	 */
	const filterUI = props.settings.isFilterable ? (
		<TextField
			canClear
			className={
				props.isSelectAllable
					? classes['multi-select__text-field--block']
					: classes['multi-select__text-field--block--leading']
			}
			help={filterTextFieldHelp}
			inputProps={{
				id: `multi-select${
					props.title && typeof props.title !== 'undefined'
						? `__${formatForID(props.title)}`
						: ''
				}__filter`,
			}}
			label='Filter Results'
			trailingIcon={
				<Icon
					icon='filter_alt'
					id={`multi-select${
						props.title && typeof props.title !== 'undefined'
							? `__${formatForID(props.title)}`
							: ''
					}__filter__trailing-icon`}
				/>
			}
			onChange={e => handleFilter(e)}
			withoutFormik={withoutFormik}
		/>
	) : null;

	/**
	 * search textbox
	 */
	const searchUI = props.settings.isSearchable ? (
		<TextField
			className={classes['multi-select__text-field--flex']}
			fullWidth={true}
			help={searchTextFieldHelp}
			inputProps={{
				id: `multi-select${
					props.title && typeof props.title !== 'undefined'
						? `__${formatForID(props.title)}`
						: ''
				}__search`,
				placeholder: 'Search For Results',
				onKeyPress: e => (e.key === 'Enter' ? handleSearch(e) : null),
			}}
			trailingIcon={
				<Icon
					icon='search'
					id={`multi-select${
						props.title && typeof props.title !== 'undefined'
							? `__${formatForID(props.title)}`
							: ''
					}__search__trailing-icon`}
				/>
			}
			onTrailingIconSelect={() => {
				let event = {
					target: {
						value: refTextFieldSearch.current.getValue(),
					},
				};
				handleSearch(event);
			}}
			ref={refTextFieldSearch}
			withoutFormik={withoutFormik}
		/>
	) : null;

	/**
	 * toggling button to show/hide contents
	 */
	const openToggleUI = (
		<IconButton
			onClick={handleOpenToggle}
			title='Toggle Multi-Select'
			id={`multi-select${
				props.title && typeof props.title !== 'undefined'
					? `__${formatForID(props.title)}`
					: ''
			}__expand-toggle`}
			aria-label='Toggle Multi-Select'
		>
			{isOpen ? <Icon icon='expand_less' /> : <Icon icon='expand_more' />}
		</IconButton>
	);

	/**
	 * toolbar section to display additional features like filtering, searching, etc.
	 */
	const toolbarUI =
		isOpen && (selectAllUI || filterUI || searchUI) ? (
			<StyleAuxiliary classes={styleToolBar}>
				{/* select all checkbox */}
				{selectAllUI}
				{/* filter textbox */}
				{filterUI}
				{/* search textbox */}
				{searchUI}
			</StyleAuxiliary>
		) : null;

	/**
	 * display the options list/table
	 */
	const optionsUI = isOpen ? generateOptionsUI() : null;

	//rendering
	return (
		<Fragment>
			<WithLoadingHandler showChildren={!isLoading} isLoading={isLoading}>
				{/* title */}
				{props.title && (
					<Headline6
						className={classes['multi-select__label--title']}
					>
						{props.title}
					</Headline6>
				)}
				{/* body */}
				<div data-test='multiselectBody' onBlur={props.onBlur}>
					<div
						className={classnames({
							[`${classes['multi-select']}`]: true,
						})}
						tabIndex='0'
					>
						{/* header */}
						<Headline3
							className={classes['multi-select__label']}
							style={
								displayActionLabel()
									? { display: 'none' }
									: null
							}
						>
							{props.actionTitle}
						</Headline3>
						{/* selections */}
						<div className={classes['multi-select__chip-set']}>
							{selectionsUI}
						</div>
						{/* toggle open/closed */}
						<div className={classes['multi-select__toggle']}>
							{openToggleUI}
						</div>
					</div>
					{/* content */}
					<Fragment>
						{/* toolbar */}
						{toolbarUI}

						{/* results list */}
						{optionsUI}
					</Fragment>
				</div>
			</WithLoadingHandler>
		</Fragment>
	);
};

Multiselect.propTypes = multiselectPropTypes;

export default Multiselect;
